<template>
  <button
    :class="{
      button: true,
      'numerai-button': true,
    }"
    :disabled="disabled"
    :title="disabled ? disabledTooltip : enabledTooltip"
    type="button"
    @click="invokeLambda"
  >
    <v-icon v-if="currentRequestId" class="pr-2 spinner">
      mdi-loading mdi-spin
    </v-icon>
    <v-icon v-if="showSuccess" class="pr-2 check">
      mdi-check
    </v-icon>
    <v-icon v-if="showError" class="pr-2 alert">
      mdi-alert-circle
    </v-icon>
    <v-icon v-if="showCaution" class="pr-2 caution">
      mdi-alert-circle
    </v-icon>
    <slot>Invoke</slot>
    <span class="spinner">
      <span></span>
      <span></span>
    </span>
  </button>
</template>

<script>
export default {
  emits: ['update-compute-data'],
  name: 'ComputeInvokeButton',
  props: {
    styled: {
      type: Boolean,
      required: false,
      default: false,
    },
    modelId: {
      type: String,
      required: true,
      default: null,
    },
    invocationType: {
      type: String,
      required: true,
    },
    numerapiVersion: {
      type: String,
      required: false,
      default: null,
    },
    disabledTooltip: {
      type: String,
      default:
        'Diagnostics requires numerapi version 2.12.20 or higher. ' +
        'Redeploy your model with the latest numerapi to enable diagnostics',
    },
    enabledTooltip: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      currentRequestId: null,
      pollInterval: null,
      showSuccess: false,
      showError: false,
      showCaution: false,
      disabled: false,
      pollStartTime: null,
    }
  },
  created() {
    this.disabled =
      !this.numerapiVersionHasDiagnostics() &&
      this.invocationType === 'diagnostics'
  },
  methods: {
    async invokeLambda() {
      // set a phony request id so spinner starts instantly
      // instead of waiting for first api response
      this.currentRequestId = 'invoking lol'
      try {
        let res = await this.$client.invokeModelLambda(
          this.modelId,
          this.invocationType,
        )
        this.currentRequestId = res
      } catch (err) {
        this.currentRequestId = null
        // TODO: set red X icon or something
        this.$emit('update-compute-data')
        console.error(err)
      }
      // start polling using request id until done or error
      this.pollInterval = await this.pollInvocationStatus()
      // track polling start time so we can timeout after two minutes
      this.pollStartTime = Date.now()
      // TODO: update last invoked, etc.
    },
    async fetchInvocationStatus() {
      if (!this.currentRequestId) {
        return
      }

      // if still polling after two minutes, stop polling
      // and show caution status
      if (Date.now() - this.pollStartTime > 120000) {
        this.$emit('update-compute-data')
        clearInterval(this.pollInterval)
        this.currentRequestId = null
        this.showCaution = true
        setTimeout(() => {
          this.showCaution = false
        }, 5000)
      }

      let res = await this.$client.invocationStatus(this.currentRequestId)
      if (res.status === 'complete') {
        this.$emit('update-compute-data')
        clearInterval(this.pollInterval)
        this.currentRequestId = null
        this.showSuccess = true
        setTimeout(() => {
          this.showSuccess = false
        }, 5000)
        // emit event to parent table to update compute status and invocations
      } else if (res.status === 'error') {
        this.$emit('update-compute-data')
        clearInterval(this.pollInterval)
        this.showError = true
        setTimeout(() => {
          this.showError = false
        }, 5000)
        this.currentRequestId = null
      }
    },
    async pollInvocationStatus() {
      setInterval(() => {
        this.fetchInvocationStatus()
      }, 1000)
    },
    numerapiVersionHasDiagnostics() {
      if (this.numerapiVersion) {
        let splitVersion = this.numerapiVersion.split('.')
        let major = parseInt(splitVersion[0])
        let minor = parseInt(splitVersion[1])
        let patch = parseInt(splitVersion[2])
        return major >= 2 && minor >= 12 && patch >= 20
      }
      return false
    },
  },
}
</script>

<style scoped>
/* reset */
button {
  font-size: 100%;
  margin: 0;
  overflow: visible;
  text-transform: none;
  -webkit-appearance: button;
}
button::-moz-focus-inner {
  border-style: none;
  padding: 0;
}
button:-moz-focusring {
  outline: 1px dotted ButtonText;
}

.spinner {
  color: #00bcd0;
}

.check {
  color: rgb(10, 179, 7);
}

.alert {
  color: #d24141;
}

.caution {
  color: #dab90d;
}
</style>
